import { useState, useEffect } from "react";
import { Play, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const YOUTUBE_VIDEO_ID = "U91SY0aJmkk";

export function ShortcodeGuideButton() {
  const [isOpen, setIsOpen] = useState(false);

  // Handle escape key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  // Lock body scroll while modal is open
  useEffect(() => {
    if (isOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [isOpen]);

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 15, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="fixed bottom-6 right-4 sm:right-6 z-40"
      >
        <motion.button
          whileHover={{ scale: 1.02, y: -2 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setIsOpen(true)}
          className="
            group relative
            flex items-center gap-3
            h-[52px] pl-2 pr-5
            rounded-full
            bg-gradient-to-b from-white to-[#fafafa]
            border border-black/[0.06]
            shadow-[0_2px_8px_rgba(0,0,0,0.06),0_8px_24px_rgba(0,0,0,0.06)]
            hover:shadow-[0_4px_14px_rgba(237,90,41,0.18),0_10px_30px_rgba(0,0,0,0.08)]
            transition-shadow duration-300
            focus:outline-none
            focus:ring-2
            focus:ring-[#ed5a29]/30
            focus:ring-offset-2
          "
          aria-label="Watch shortcode guide video"
          title="Shortcode Guide"
        >
          {/* Icon */}
          <div className="relative flex items-center justify-center w-9 h-9 flex-shrink-0 rounded-full bg-gradient-to-br from-[#ed5a29] to-[#d84a1a] shadow-[0_2px_6px_rgba(237,90,41,0.35)] transition-transform duration-300 group-hover:scale-105">
            <Play className="w-[13px] h-[13px] text-white fill-current translate-x-[1px]" />
          </div>

          {/* Text */}
          <span className="text-[13.5px] sm:text-[14px] font-semibold text-[#263238] whitespace-nowrap">
            Using Shortcode to Display Your Form
          </span>
        </motion.button>
      </motion.div>

      {/* Modal */}
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 md:p-12">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />

            {/* Modal Container */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative w-full max-w-[700px] bg-white rounded-[20px] shadow-2xl overflow-hidden flex flex-col max-h-full"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-5 border-b border-gray-100">
                <h3 className="text-xl font-bold text-gray-900 font-sora">
                  Mobimint Form Builder
                </h3>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#ed5a29]"
                  aria-label="Close modal"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Video content */}
              <div className="w-full p-4 sm:p-6">
                <div className="relative w-full overflow-hidden rounded-[14px] bg-black" style={{ paddingTop: "56.25%" }}>
                  <iframe
                    className="absolute inset-0 w-full h-full"
                    src={`https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}?si=ImmNeiDcNRRpS3G3${isOpen ? "&autoplay=1" : ""}`}
                    title="How to use shortcode in form"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />

                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
